import type { CatalogEntry } from '@caos/ui';
import type { Navigate } from './setup-surface.js';
import { renderRosterPage, placeInConsolePane, type RosterPageSpec } from './roster-page.js';
import { renderComponentReference } from './component-reference.js';

/**
 * CAOS-381 / CAOS-387 / CAOS-461 / CAOS-562 / CAOS-572 / CAOS-714 / CAOS-787 — the Component
 * Library, in the workspace.
 *
 * CAOS-714 — this module is the Setup-specific RESIDUE, not the page. The LIST (grouping, board,
 * sort, search) is an authored `rosterPage` component (`component_library_list`, shipped in
 * `caos-pkg-setup/src/rosterPages/`) read + drawn by the SAME generic `renderRosterPage` renderer
 * every other Setup browse (Roles, Sharing, Value Sets) already uses — CAOS-382's split, applied
 * here. The org's installed components themselves are read from the `components` control-plane
 * noun (CAOS-714, `kernel/src/api/component-library-routes.ts`), which also computes the three
 * presentation strings a plain JSON-path column cannot derive (`kind`, `placementLabel`,
 * `placesLabel` — see `kernel/src/describe/component-inspection.ts`): the SAME derivation that used
 * to live here, moved to the one place that can compute it once for every reader.
 *
 * CAOS-787 — the remaining architecture debt CAOS-714 left behind: the rosterPage existed, but
 * this surface's nav rail entry was still `setup-surface.ts`'s own hardcoded `SECTION_GROUPS`
 * (`heading: 'Platform'`) rather than a real package-declared `setup` capability inject claim, the
 * same debt CAOS-785/CAOS-786 already paid down for Object Manager/Page Layouts. That claim
 * (`caos-pkg-setup/src/package/pkg_setup.package.json`, `group: 'platform'`) now draws the rail
 * entry, and this module — renamed to match `setup-object-manager.ts`/`setup-page-layouts.ts` — is
 * the thin route-shape bridge those two established. The per-piece DETAIL route is deliberately
 * NOT a generic `rosterDetailPage`: a working, configured, LIVE example is the whole point of a
 * piece's own page, and that is not something a declarative JSON record can express — so what is
 * left here is what CAOS-382's own `setup-sharing.ts` residue is: the two component keys, the
 * `/setup/component-library[/<key>]` route shape, and — because a piece's OWN page is a real
 * three-tab working reference (component-reference.ts, CAOS-572), not a generic breadcrumb+kv-rows
 * record — the composition that adds the org-specific "About this piece" / "What a page can set" /
 * problem+deprecation sections around that shared, already-generic reference renderer. Nothing here
 * fetches its own bespoke route or projects a manifest by hand; both are the `components` noun's job.
 *
 * ── THE FAILURE THIS PAGE EXISTS TO PREVENT ──────────────────────────────────
 * Somebody building a component that already exists, or five people building the same thing five
 * ways. Both happen for the same reason: a search for the word THEY use returns nothing, and an
 * empty result reads as "we do not have one". So the list is searched by what a piece IS called and
 * what it is FOR, read off the org's own installed metadata.
 */

const ROUTE = '/setup/component-library';
const LIST_COMPONENT_KEY = 'component_library_list';

/** `/setup/component-library/<key>` -> the piece being read. */
function detailKey(pathname: string): string | undefined {
  return pathname.split('/').filter(Boolean)[2];
}

function link(text: string, href: string, navigate?: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'setup-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

function note(text: string): HTMLElement {
  // Deliberately NOT `.setup-note` — that class is the example section's own "no example" signal
  // (CAOS-631), and this note lives in a different section entirely (what a page can set).
  const p = document.createElement('p');
  p.className = 'cl-note';
  p.textContent = text;
  return p;
}

/** One input a page may set on a component — the `components get` payload's own shape, verbatim. */
interface ComponentInputRow {
  readonly name: string;
  readonly label: string;
  readonly type?: string;
  readonly required: boolean;
  readonly help?: string;
  readonly deprecated?: boolean;
}

/**
 * One component, exactly as `components get` (kernel/src/describe/component-inspection.ts) serves
 * it — including the three presentation strings (`kind`, `placementLabel`, `placesLabel`) that
 * noun computes once so this page never re-derives them from the raw manifest fields.
 */
interface ComponentRow {
  readonly key: string;
  readonly label: string;
  readonly purpose: string;
  readonly group: string;
  readonly tag: string;
  readonly exposed: boolean;
  readonly placements?: readonly string[];
  readonly inputs?: readonly ComponentInputRow[];
  readonly problems?: readonly string[];
  readonly deprecated?: boolean;
  readonly kind: string;
  readonly placementLabel: string;
  readonly placesLabel: string;
  /**
   * CAOS-430 — what the product itself does with this piece, measured from its own surfaces rather
   * than maintained by hand. Always a sentence, including for a piece the measurement does not
   * cover: "nothing uses it" and "nobody looked" are different answers and a reader would act on
   * them differently, so the noun never collapses the second into the first.
   */
  readonly usageLabel: string;
}

/**
 * What is wrong with a piece whose manifest is incomplete, said plainly and in one place.
 *
 * The piece IS installed. Saying so matters: the alternative — leaving it out of the list — is
 * what made a page report it as missing and tell somebody to install a package they already have.
 */
function problemsBanner(row: ComponentRow): HTMLElement | undefined {
  if (!row.problems || row.problems.length === 0) return undefined;
  const banner = document.createElement('caos-notice-banner');
  banner.setAttribute('intent', 'danger');
  banner.textContent =
    `This piece is installed, but the package that provides it did not describe it fully: ` +
    `${row.problems.join('; ')}. Until that is corrected it cannot be placed on a page. ` +
    `Re-installing will not help — the fix belongs in the package.`;
  return banner;
}

/**
 * CAOS-461 — a deprecated piece is still fully working and still resolving; the notice exists so
 * an author sees it BEFORE choosing it again for a new placement, not to warn anyone off what is
 * already placed. Distinct from {@link problemsBanner}: a problem means the manifest is broken,
 * deprecation means the manifest is fine and the platform is asking authors to stop reaching for it.
 */
function deprecatedBanner(row: ComponentRow): HTMLElement | undefined {
  if (!row.deprecated) return undefined;
  const banner = document.createElement('caos-notice-banner');
  banner.setAttribute('intent', 'warning');
  banner.textContent =
    'This piece is deprecated. It still works and still resolves on every page that already places ' +
    'it — this is a signal to choose something else for new work, not a sign anything here is broken.';
  return banner;
}

/**
 * Adapt an org-installed component into the shape the reference page (built against the compiled
 * catalogue) expects. `file` is deliberately empty — an installed piece carries no in-workspace
 * file, and the reference page says so rather than printing a path that does not exist.
 */
function toCatalogEntry(row: ComponentRow): CatalogEntry {
  return {
    file: '',
    group: row.group,
    name: row.label,
    purpose: row.purpose,
    tags: row.exposed ? [row.tag] : [],
  };
}

// ── One piece ─────────────────────────────────────────────────────────────────────────────────────

/**
 * What a page may set on this piece when it places one.
 *
 * A component's inputs are the whole of its contract with a page — the page editor offers exactly
 * these and nothing else — so the library shows them rather than leaving a person to read source.
 * Read straight off the org's installed manifest: the reference page's own Specifications tab
 * reflects the compiled catalogue's declarations, which is a different, and for an installed piece
 * usually empty, source.
 */
function renderInputs(root: HTMLElement, row: ComponentRow): void {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', 'What a page can set');
  if (row.inputs === undefined) {
    // The honest state for a piece that has not declared its contract. Reporting "no settings"
    // here would be a claim nobody made, and the one a page author would act on and be wrong.
    section.append(
      note('This piece has not declared what a page can set on it, so the page editor offers nothing yet.'),
    );
  } else if (row.inputs.length === 0) {
    // "No settings" is a claim, and it is only this piece's to make. When the settings it declared
    // could not be read, the empty list is a symptom rather than an answer, and saying "no
    // settings" here would be the one wrong thing a page author would act on.
    section.append(
      note(
        row.problems?.length
          ? 'The settings this piece declares could not be read, so what a page can set on it is unknown.'
          : 'This piece takes no settings — a page places it and it does its job.',
      ),
    );
  } else {
    for (const input of row.inputs) {
      const kv = document.createElement('caos-key-value-row');
      kv.setAttribute('label', input.label);
      // An input that never said what kind of value it takes is reported as unstated rather than
      // shown as text — the manifest's own problems banner carries the same fault in full.
      const declared = input.type ?? 'kind not stated';
      const kind = input.required ? `${declared} · required` : declared;
      const withDeprecation = input.deprecated ? `${kind} · deprecated` : kind;
      kv.setAttribute('value', input.help ? `${withDeprecation} — ${input.help}` : withDeprecation);
      section.append(kv);
    }
  }
  root.append(section);
}

function renderDetail(root: HTMLElement, row: ComponentRow, navigate?: Navigate): void {
  // The identity, a real working example (when the compiled library has declared one), and the
  // Develop/Specifications tabs — all drawn by the reference page this library shares with the
  // still-compiled base set.
  root.append(renderComponentReference(toCatalogEntry(row), navigate));

  // CAOS-387 — a piece IS installed even when the package that provides it described it
  // incompletely; saying so, plainly, is what stops a page reporting it as missing and sending
  // somebody to re-install a package they already have.
  const banner = problemsBanner(row);
  if (banner) root.append(banner);

  // CAOS-461 — deprecation is not a problem (the manifest is fine); shown alongside, not merged
  // into, the problems banner above.
  const deprecation = deprecatedBanner(row);
  if (deprecation) root.append(deprecation);

  // What the ORG's installed manifest is authoritative for — where it is actually placed here, and
  // what a page may set on it — read straight off the `components get` row; both strings are
  // computed ONCE by that noun, not re-derived here.
  const about = document.createElement('caos-detail-section');
  about.setAttribute('heading', 'About this piece');
  const facts: [string, string][] = [
    ['Placed as', row.placementLabel],
    ['Can be placed on', row.placesLabel],
    // CAOS-430 — the honesty line. Without it every piece on this page carries the same implied
    // endorsement, and a library reads as more PROVEN than it is: nothing separates a piece placed
    // on a real screen from one that has only ever appeared in its own showcase. It sits beside
    // "Placed as" deliberately, because the two answer different questions that look alike — that
    // one is what a page may place, this one is what anything actually did.
    ['Used in this product', row.usageLabel],
    ...(row.deprecated
      ? ([['Deprecated', 'Still works and still resolves — choose something else for new work']] as [string, string][])
      : []),
  ];
  for (const [label, value] of facts) {
    const kv = document.createElement('caos-key-value-row');
    kv.setAttribute('label', label);
    kv.setAttribute('value', value);
    about.append(kv);
  }
  root.append(about);

  renderInputs(root, row);
}

function renderNotFound(root: HTMLElement, key: string, navigate?: Navigate): void {
  const header = document.createElement('caos-page-header');
  header.setAttribute('eyebrow', 'Component Library');
  header.setAttribute('heading', 'No such piece');
  header.setAttribute('description', `Nothing installed here is called “${key}”.`);
  root.append(header, link('← All pieces', ROUTE, navigate));
}

// ── The fetch seam ────────────────────────────────────────────────────────────────────────────────
// CAOS-714 — the SAME generic `/api/metadata/component-get` route `setup-value-sets.ts`/`setup-sharing.ts`
// already use for the list's own authored spec, and the new `components get` control-plane route for
// one piece's detail. There is no bespoke API knowledge left in this file beyond those two paths —
// both are named ONLY here, the same residue CAOS-382 left for Sharing.

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (path: string) => Promise<MetaResponse>;

const defaultMetaFetch: MetaFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the component-fetch seam (production wiring / deterministic tests). */
export function setSetupComponentLibraryFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
}

/** GET one authored component by type + key, or `null` on any failure (fail-soft, never a crash). */
async function fetchComponent<T extends { key: string }>(type: string, key: string): Promise<T | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(`/api/metadata/component-get?type=${type}&key=${encodeURIComponent(key)}`);
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const envelope = env.data as { key?: unknown; body?: Record<string, unknown> } | null;
  if (!envelope || typeof envelope.key !== 'string' || !envelope.body) return null;
  return { ...envelope.body, key: envelope.key } as unknown as T;
}

/** GET one installed component's row (`components get`), or `null` when this org has none by that key. */
async function fetchRow(key: string): Promise<ComponentRow | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(`/api/components/get?key=${encodeURIComponent(key)}`);
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  return (env.data as ComponentRow | undefined) ?? null;
}

// ── Entry point ───────────────────────────────────────────────────────────────────────────────────

export interface SetupComponentLibraryContext {
  readonly pathname: string;
  readonly navigate?: Navigate;
}

/**
 * Render the Component Library — the list of pieces, or the reference for one piece. Returns
 * `{ root, done }` so a test can await the full (spec/row + data) round trip before asserting, the
 * same shape every other authored Setup browse already returns.
 */
export function renderSetupComponentLibrary(
  ctx: SetupComponentLibraryContext,
): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const key = detailKey(ctx.pathname);

  const done = (async (): Promise<void> => {
    if (key) {
      const wanted = decodeURIComponent(key);
      const row = await fetchRow(wanted);
      root.replaceChildren();
      if (row) renderDetail(root, row, ctx.navigate);
      else renderNotFound(root, wanted, ctx.navigate);
      return;
    }

    const spec = await fetchComponent<RosterPageSpec>('rosterPage', LIST_COMPONENT_KEY);
    if (!spec) {
      root.replaceChildren();
      const empty = document.createElement('caos-empty-state');
      empty.setAttribute('title', 'Component Library is not available');
      empty.textContent = 'The Component Library list definition could not be read from this org’s installed metadata.';
      root.append(empty);
      return;
    }
    const { root: surface, done: renderDone } = renderRosterPage(spec, { navigate: ctx.navigate, fetch: metaFetch });
    placeInConsolePane(root, surface);
    await renderDone;
  })();

  return { root, done };
}
